/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Scale } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { usePayments } from '../../../contexts/PaymentContext';
import { safeParseObject } from '../../../utils/storageUtils.js';
import ProviderList from '../../Admin/Providers/ProviderList';
import ProviderDetails from '../../Admin/Providers/ProviderDetails';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

/**
 * Componente da Seção Jurídica
 * Permite visualização de prestadores em modo SOMENTE LEITURA
 */
const LegalProviders = () => {
    const { allUsers, preRegisteredStaff, updateStaffData } = useAuth();
    const { payments } = usePayments();
    const [providers, setProviders] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedProvider, setSelectedProvider] = useState(null);

    // Carregar prestadores (Copiado lógica do AdminProviders)
    // State for manual search (to pretend like it's fetching from cloud/external)
    const [hasSearched, setHasSearched] = useState(false);

    const handleSearch = () => {
        setIsLoading(true);
        setHasSearched(true);

        // Simulating a network request/audit process
        setTimeout(() => {
            try {
                // 1. Get manually saved provider list
                const savedProviders = safeParseObject('registeredUsers');
                const providerList = Object.values(savedProviders).filter(user => user.isStaff);

                // Calculate stats for each provider
                const providersWithStats = providerList.map(provider => {
                    const userPayments = payments.filter(p => p.userId === provider.discordId);
                    const stats = {
                        resolvedTickets: 0,
                        pendingTickets: 0,
                        approvedPayments: userPayments.filter(p => p.status === 'approved').length,
                        totalPaid: userPayments
                            .filter(p => p.status === 'paid')
                            .reduce((acc, curr) => acc + curr.totalValue, 0)
                    };
                    return { ...provider, stats };
                });

                setProviders(providersWithStats);
            } catch (error) {
                console.error("Error loading providers:", error);
            } finally {
                setIsLoading(false);
            }
        }, 1500); // 1.5s delay for "cloud" effect
    };

    // Funções Dummy para bloquear ações (embora botões estejam ocultos visualmente)
    const handleUpdate = async () => { console.warn("Ação bloqueada: Somente Leitura"); };
    const handleSuspend = async () => { console.warn("Ação bloqueada: Somente Leitura"); };
    const handleReactivate = async () => { console.warn("Ação bloqueada: Somente Leitura"); };
    const handleDelete = async () => { console.warn("Ação bloqueada: Somente Leitura"); };
    const handleImpersonate = async () => { console.warn("Ação bloqueada: Somente Leitura"); };

    return (
        <div className="space-y-8 animate-in fade-in duration-700">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-4xl font-bold bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 bg-clip-text text-transparent flex items-center gap-3">
                        <Scale className="h-10 w-10 text-emerald-500" />
                        Departamento Jurídico
                    </h1>
                    <p className="text-gray-400 mt-2 text-lg">
                        Visualização e Auditoria de Contratos e Prestadores
                    </p>
                </div>

                {/* Manual Search Button */}
                <div className="flex items-center gap-4">
                    {!hasSearched ? (
                        <div className="flex items-center gap-2">
                            <div className="flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 px-3 py-1.5 rounded-full animate-pulse">
                                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                <span className="text-xs text-blue-400 font-bold uppercase">Conectado à Nuvem</span>
                            </div>
                            <Button
                                onClick={handleSearch}
                                disabled={isLoading}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-900/20"
                            >
                                {isLoading ? (
                                    <>Carregando...</>
                                ) : (
                                    <>
                                        <Users className="w-4 h-4 mr-2" />
                                        Carregar Prestadores
                                    </>
                                )}
                            </Button>
                        </div>
                    ) : (
                        <Button
                            variant="outline"
                            onClick={handleSearch}
                            disabled={isLoading}
                            className="border-gray-700 hover:bg-gray-800 text-gray-400"
                        >
                            <Scale className="w-4 h-4 mr-2" />
                            Atualizar Lista
                        </Button>
                    )}
                </div>
            </div>

            {/* Lista Reutilizada */}
            {hasSearched && (
                <Card className="bg-black/40 border-white/10 backdrop-blur-md overflow-hidden ring-1 ring-white/5 animate-in fade-in slide-in-from-bottom-4 duration-700">
                    <CardContent className="p-6">
                        <ProviderList
                            providers={providers}
                            onSelectProvider={setSelectedProvider}
                        />
                    </CardContent>
                </Card>
            )}

            {/* Detalhes (Modo Leitura) */}
            <AnimatePresence>
                {selectedProvider && (
                    <ProviderDetails
                        provider={selectedProvider}
                        userPayments={payments.filter(p => p.userId === selectedProvider.discordId)}
                        onClose={() => setSelectedProvider(null)}

                        // Passando funções dummy e flag readOnly
                        onUpdate={handleUpdate}
                        onSuspend={handleSuspend}
                        onReactivate={handleReactivate}
                        onDelete={handleDelete}
                        onImpersonate={handleImpersonate}
                        readOnly={true}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

export default LegalProviders;
